import { Menu, Wifi, WifiOff } from 'lucide-react';

import logoLight from '@/assets/images/logo-light.png';
import { useCurrentUser } from '@/shared/auth';
import { Avatar, AvatarFallback } from '@/shared/ui/avatar';

interface FieldTopbarProps {
  onMenuToggle: () => void;
  onProfileOpen: () => void;
  isOnline: boolean;
}

function initials(first?: string, last?: string): string {
  return `${first?.[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase() || '?';
}

/**
 * Sticky top bar of the field-app PWA. Renders the brand mark, a connectivity
 * indicator, and the worker's avatar.
 */
export function FieldTopbar({
  onMenuToggle,
  onProfileOpen,
  isOnline,
}: FieldTopbarProps) {
  const user = useCurrentUser();

  return (
    <header
      className="tw-sticky tw-top-0 tw-z-40 tw-flex tw-h-16 tw-w-full tw-shrink-0 tw-items-center tw-justify-between tw-border-b tw-border-border/70 tw-bg-card/90 tw-px-3 tw-shadow-[0_2px_10px_rgba(15,23,42,0.05)] tw-backdrop-blur-xl"
      style={{ paddingTop: 'env(safe-area-inset-top, 0)' }}
    >
      {/* Menu */}
      <button
        type="button"
        aria-label="Open menu"
        onClick={onMenuToggle}
        className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-xl tw-text-foreground/70 tw-transition-colors hover:tw-bg-muted active:tw-scale-95"
      >
        <Menu className="tw-h-[22px] tw-w-[22px]" strokeWidth={2.2} />
      </button>

      {/* Brand */}
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-leading-none">
        <img src={logoLight} alt="Euro Scaffolds" className="tw-h-6 tw-w-auto" />
        <span className="tw-mt-1 tw-text-[9px] tw-font-bold tw-uppercase tw-tracking-[0.2em] tw-text-muted-foreground">
          Field App
        </span>
      </div>

      {/* Status + profile */}
      <div className="tw-flex tw-items-center tw-gap-2.5">
        <span
          aria-label={isOnline ? 'Online' : 'Offline'}
          title={isOnline ? 'Online' : 'Offline'}
          className="tw-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-muted/70 tw-px-2 tw-py-1"
        >
          <span
            className={
              isOnline
                ? 'tw-h-2 tw-w-2 tw-rounded-full tw-bg-emerald-500 tw-shadow-[0_0_0_3px_rgba(16,185,129,0.18)]'
                : 'tw-h-2 tw-w-2 tw-rounded-full tw-bg-amber-500 tw-shadow-[0_0_0_3px_rgba(245,158,11,0.18)]'
            }
          />
          {isOnline ? (
            <Wifi className="tw-h-3.5 tw-w-3.5 tw-text-emerald-600" />
          ) : (
            <WifiOff className="tw-h-3.5 tw-w-3.5 tw-text-amber-600" />
          )}
        </span>

        <button
          type="button"
          onClick={onProfileOpen}
          aria-label="Open profile"
          className="tw-relative tw-rounded-full tw-outline-none tw-transition-transform focus-visible:tw-ring-2 focus-visible:tw-ring-ring active:tw-scale-95"
        >
          <Avatar className="tw-h-9 tw-w-9">
            <AvatarFallback className="tw-bg-gradient-to-br tw-from-primary tw-to-sky-400 tw-text-xs tw-font-bold tw-text-white">
              {initials(user?.firstName, user?.lastName)}
            </AvatarFallback>
          </Avatar>
          {isOnline ? (
            <span className="tw-absolute tw--bottom-0.5 tw--right-0.5 tw-h-3 tw-w-3 tw-rounded-full tw-border-2 tw-border-card tw-bg-emerald-500" />
          ) : null}
        </button>
      </div>
    </header>
  );
}
